import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";

interface SlackUserMatch {
  slackId: string;
  name: string;
  email: string;
  matchType: "existing_slack_id" | "existing_email" | "new";
  employeeId?: string;
  conflict?: string;
  directManagerId?: string;
  startOnboarding?: boolean;
}

interface SlackSyncResult {
  toCreate: SlackUserMatch[];
  toUpdate: SlackUserMatch[];
  conflicts: SlackUserMatch[];
}

interface Employee {
  id: string;
  fullName: string;
  corporateEmail: string | null;
  status: string;
}

interface UserSelection {
  selected: boolean;
  onboarding: boolean;
  managerId: string;
}

interface SlackSyncModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function collectSelectedUsers(
  syncResult: SlackSyncResult,
  selections: Record<string, UserSelection>,
): SlackUserMatch[] {
  const result: SlackUserMatch[] = [];
  for (const user of syncResult.toCreate) {
    const sel = selections[user.slackId];
    if (!sel?.selected) continue;
    result.push({
      ...user,
      directManagerId: sel.managerId || undefined,
      startOnboarding: sel.onboarding,
    });
  }
  for (const user of syncResult.toUpdate) {
    const sel = selections[user.slackId];
    if (!sel?.selected) continue;
    result.push({
      ...user,
      directManagerId: sel.managerId || undefined,
    });
  }
  return result;
}

export function SlackSyncModal({ open, onOpenChange }: SlackSyncModalProps) {
  const { toast } = useToast();
  const [selections, setSelections] = useState<Record<string, UserSelection>>({});

  const { data: syncResult, isLoading: isFetching } = useQuery<SlackSyncResult>({
    ...orpc.employees.syncSlack.queryOptions(),
    enabled: open,
  });

  const { data: employeesData } = useQuery<Employee[]>({
    ...orpc.employees.list.queryOptions(),
    enabled: open,
  });

  const employees: Employee[] = Array.isArray(employeesData) ? employeesData : [];
  const activeEmployees = employees.filter((e) => e.status === "active");

  const managerOptions = activeEmployees.map((e) => ({
    value: e.id,
    label: `${e.fullName}${e.corporateEmail ? ` (${e.corporateEmail})` : ""}`,
  }));

  const bulkSyncMutation = useMutation({
    ...orpc.employees.bulkSyncSlack.mutationOptions(),
    onSuccess: (data: {
      created: number;
      updated: number;
      errors: { email: string; error: string }[];
    }) => {
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      toast({
        description: `${data.created} cadastrado${data.created === 1 ? "" : "s"} · ${data.updated} atualizado${data.updated === 1 ? "" : "s"}.`,
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        description: error.message || "Falha ao sincronizar com Slack. Tente novamente.",
      });
    },
  });

  useEffect(() => {
    if (syncResult) {
      const initial: Record<string, UserSelection> = {};
      for (const user of syncResult.toCreate) {
        initial[user.slackId] = { selected: false, onboarding: false, managerId: "" };
      }
      for (const user of syncResult.toUpdate) {
        initial[user.slackId] = { selected: true, onboarding: false, managerId: "" };
      }
      setSelections(initial);
    }
  }, [syncResult]);

  const updateSelection = (slackId: string, field: keyof UserSelection, value: any) => {
    setSelections((prev) => ({
      ...prev,
      [slackId]: { ...prev[slackId], [field]: value },
    }));
  };

  const handleSelectAll = () => {
    if (!syncResult) return;
    const allNew = syncResult.toCreate;
    const allSelected = allNew.every((u) => selections[u.slackId]?.selected);
    setSelections((prev) => {
      const next = { ...prev };
      for (const user of allNew) {
        next[user.slackId] = { ...next[user.slackId], selected: !allSelected };
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (!syncResult) return;

    const usersToSync = collectSelectedUsers(syncResult, selections);

    if (usersToSync.length === 0) {
      toast({ description: "Selecione pelo menos um usuário do Slack.", variant: "destructive" });
      return;
    }

    const newWithoutManager = usersToSync.filter(
      (u) => u.matchType === "new" && !u.directManagerId,
    );
    if (newWithoutManager.length > 0) {
      toast({
        description: `Selecione o líder para: ${newWithoutManager.map((u) => u.name).join(", ")}.`,
        variant: "destructive",
      });
      return;
    }

    bulkSyncMutation.mutate({ selectedUsers: usersToSync });
  };

  const selectedNewCount = syncResult
    ? syncResult.toCreate.filter((u) => selections[u.slackId]?.selected).length
    : 0;
  const selectedUpdateCount = syncResult
    ? syncResult.toUpdate.filter((u) => selections[u.slackId]?.selected).length
    : 0;
  const selectedCount = selectedNewCount + selectedUpdateCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass-modal max-w-4xl max-h-[80vh] overflow-y-auto"
        overlayClassName="glass-scrim"
        data-testid="dialog-slack-sync"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle data-testid="text-slack-sync-title">Sincronizar com Slack</DialogTitle>
            <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
          </div>
          <DialogDescription data-testid="text-slack-sync-description">
            Revise os usuários do Slack e selecione quem cadastrar. Para novos colaboradores, defina
            o líder e, se quiser, inicie o onboarding em seguida.
          </DialogDescription>
        </DialogHeader>

        {isFetching && (
          <div className="flex items-center justify-center py-10" data-testid="loading-slack-sync">
            <span className="body-strong text-[hsl(var(--ink-primary))]">
              Buscando usuários do Slack…
            </span>
          </div>
        )}

        {!isFetching && syncResult && (
          <div className="space-y-6">
            <p className="text-sm text-[hsl(var(--ink-primary))]" data-testid="summary-slack-sync">
              <span className="body-strong text-[hsl(var(--forest-pulse))]">
                <span className="num-mono">{syncResult.toCreate.length}</span> novos
              </span>{" "}
              ·{" "}
              <span className="body-strong text-[hsl(var(--ink-primary))]">
                <span className="num-mono">{syncResult.toUpdate.length}</span> para atualizar
              </span>
              {syncResult.conflicts.length > 0 && (
                <>
                  {" "}
                  ·{" "}
                  <span className="body-strong text-[hsl(var(--alert-red))]">
                    <span className="num-mono">{syncResult.conflicts.length}</span> com conflito
                  </span>
                </>
              )}
              .
            </p>

            {syncResult.toCreate.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center justify-between border-b border-[hsl(var(--card-line))] pb-2">
                  <h3 className="caption-mono" data-testid="text-new-users-header">
                    Novos colaboradores
                  </h3>
                  <button
                    type="button"
                    className="text-sm underline text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))]"
                    onClick={handleSelectAll}
                    data-testid="button-select-all-new"
                  >
                    {syncResult.toCreate.every((u) => selections[u.slackId]?.selected)
                      ? "desmarcar todos"
                      : "selecionar todos"}
                  </button>
                </div>

                <ul className="divide-y divide-[hsl(var(--card-line))]">
                  {syncResult.toCreate.map((user) => {
                    const sel = selections[user.slackId] || {
                      selected: false,
                      onboarding: false,
                      managerId: "",
                    };
                    return (
                      <li
                        key={user.slackId}
                        className={`py-3 ${sel.selected ? "bg-[hsl(var(--sand-step))]/60" : ""}`}
                        data-testid={`card-new-user-${user.slackId}`}
                      >
                        <div className="flex items-start gap-3 px-2">
                          <input
                            type="checkbox"
                            className="mt-1 h-3.5 w-3.5 accent-[hsl(var(--comp-coral))]"
                            checked={sel.selected}
                            onChange={(e) =>
                              updateSelection(user.slackId, "selected", e.target.checked)
                            }
                            data-testid={`checkbox-import-${user.slackId}`}
                          />
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center gap-3">
                              <span
                                className="body-strong truncate text-[hsl(var(--ink-primary))]"
                                data-testid={`text-name-${user.slackId}`}
                              >
                                {user.name}
                              </span>
                              <span
                                className="body-strong text-[hsl(var(--forest-pulse))]"
                                data-testid={`badge-new-${user.slackId}`}
                              >
                                Novo
                              </span>
                            </div>
                            <p
                              className="text-sm text-[hsl(var(--ink-muted))] truncate"
                              data-testid={`text-email-${user.slackId}`}
                            >
                              {user.email}
                            </p>

                            {sel.selected && (
                              <div className="space-y-3 pt-2">
                                <label
                                  htmlFor={`onboarding-${user.slackId}`}
                                  className="flex items-center gap-2 text-sm cursor-pointer"
                                  data-testid={`label-onboarding-${user.slackId}`}
                                >
                                  <input
                                    id={`onboarding-${user.slackId}`}
                                    type="checkbox"
                                    className="h-3.5 w-3.5 accent-[hsl(var(--comp-coral))]"
                                    checked={sel.onboarding}
                                    onChange={(e) =>
                                      updateSelection(user.slackId, "onboarding", e.target.checked)
                                    }
                                    data-testid={`checkbox-onboarding-${user.slackId}`}
                                  />
                                  Iniciar onboarding em seguida
                                </label>
                                <div className="space-y-1">
                                  <span
                                    className="caption-mono"
                                    data-testid={`label-manager-${user.slackId}`}
                                  >
                                    Líder
                                  </span>
                                  <SearchableSelect
                                    value={sel.managerId}
                                    onValueChange={(val) =>
                                      updateSelection(user.slackId, "managerId", val)
                                    }
                                    options={managerOptions}
                                    placeholder="Selecione o líder…"
                                    searchPlaceholder="Buscar…"
                                    emptyMessage="Nenhum colaborador encontrado"
                                    data-testid={`select-manager-${user.slackId}`}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {syncResult.toUpdate.length > 0 && (
              <section className="space-y-3">
                <h3
                  className="caption-mono border-b border-[hsl(var(--card-line))] pb-2"
                  data-testid="text-update-users-header"
                >
                  Atualizar Slack ID
                </h3>
                <ul className="divide-y divide-[hsl(var(--card-line))]">
                  {syncResult.toUpdate.map((user) => {
                    const sel = selections[user.slackId] || {
                      selected: true,
                      onboarding: false,
                      managerId: "",
                    };
                    return (
                      <li
                        key={user.slackId}
                        className={`flex items-center gap-3 py-3 px-2 ${sel.selected ? "bg-[hsl(var(--sand-step))]/60" : ""}`}
                        data-testid={`card-update-user-${user.slackId}`}
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-[hsl(var(--comp-coral))]"
                          checked={sel.selected}
                          onChange={(e) =>
                            updateSelection(user.slackId, "selected", e.target.checked)
                          }
                          data-testid={`checkbox-update-${user.slackId}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span
                              className="body-strong truncate text-[hsl(var(--ink-primary))]"
                              data-testid={`text-name-${user.slackId}`}
                            >
                              {user.name}
                            </span>
                            <span
                              className="body-strong text-[hsl(var(--ink-primary))]"
                              data-testid={`badge-update-${user.slackId}`}
                            >
                              Atualizar
                            </span>
                          </div>
                          <p
                            className="text-sm text-[hsl(var(--ink-muted))] truncate"
                            data-testid={`text-email-${user.slackId}`}
                          >
                            {user.email}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {syncResult.conflicts.length > 0 && (
              <section className="space-y-3">
                <h3
                  className="caption-mono border-b border-[hsl(var(--card-line))] pb-2"
                  data-testid="text-conflicts-header"
                >
                  Conflitos
                </h3>
                <ul className="divide-y divide-[hsl(var(--card-line))]">
                  {syncResult.conflicts.map((user) => (
                    <li
                      key={user.slackId}
                      className="py-3 px-2"
                      data-testid={`card-conflict-user-${user.slackId}`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className="body-strong text-[hsl(var(--ink-primary))] truncate"
                          data-testid={`text-name-${user.slackId}`}
                        >
                          {user.name}
                        </span>
                        <span className="body-strong text-[hsl(var(--alert-red))]">Conflito</span>
                      </div>
                      <p
                        className="text-sm text-[hsl(var(--ink-muted))] truncate"
                        data-testid={`text-email-${user.slackId}`}
                      >
                        {user.email}
                      </p>
                      <p
                        className="text-xs text-[hsl(var(--alert-red))] mt-1"
                        data-testid={`text-conflict-${user.slackId}`}
                      >
                        {user.conflict}
                      </p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {syncResult.toCreate.length === 0 &&
              syncResult.toUpdate.length === 0 &&
              syncResult.conflicts.length === 0 && (
                <p
                  className="text-sm text-[hsl(var(--ink-muted))] text-center py-8"
                  data-testid="text-no-users"
                >
                  Nenhum novo usuário do Slack para sincronizar. Todos já estão cadastrados.
                </p>
              )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-cancel-sync"
          >
            Fechar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isFetching || bulkSyncMutation.isPending || selectedCount === 0}
            data-testid="button-confirm-sync"
          >
            {bulkSyncMutation.isPending ? "Sincronizando…" : `Sincronizar ${selectedCount}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
